import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { SlidersHorizontal, X } from "lucide-react";

const PRICE_BOUNDS = [0, 100000];
const YEAR_BOUNDS = [2005, new Date().getFullYear()];

export default function Inventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [options, setOptions] = useState({ makes: [], body_types: [], fuel_types: [], transmissions: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => ({
    make: searchParams.get("make") || "",
    body_type: searchParams.get("body_type") || "",
    fuel_type: searchParams.get("fuel_type") || "",
    transmission: searchParams.get("transmission") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    min_year: searchParams.get("min_year") || "",
    max_year: searchParams.get("max_year") || "",
    sort: searchParams.get("sort") || "newest",
  }), [searchParams]);

  useEffect(() => {
    api.get("/vehicles/filters/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
    api.get("/vehicles", { params }).then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "" || value == null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const clearAll = () => setSearchParams({});

  const activeCount = Object.entries(filters).filter(([k, v]) => v && k !== "sort").length;

  const FilterPanel = (
    <div className="space-y-7" data-testid="filter-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest">Filter</h3>
        {activeCount > 0 && (
          <button onClick={clearAll} data-testid="filter-clear" className="text-xs text-red-600 hover:underline">
            Zurücksetzen ({activeCount})
          </button>
        )}
      </div>

      <FilterSelect label="Marke" value={filters.make} onChange={(v) => setFilter("make", v)} options={options.makes} testId="filter-make" />
      <FilterSelect label="Karosserie" value={filters.body_type} onChange={(v) => setFilter("body_type", v)} options={options.body_types} testId="filter-bodytype" />
      <FilterSelect label="Kraftstoff" value={filters.fuel_type} onChange={(v) => setFilter("fuel_type", v)} options={options.fuel_types} testId="filter-fuel" />
      <FilterSelect label="Getriebe" value={filters.transmission} onChange={(v) => setFilter("transmission", v)} options={options.transmissions} testId="filter-transmission" />

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3 block">Preis (€)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder={PRICE_BOUNDS[0]} value={filters.min_price} onChange={(e) => setFilter("min_price", e.target.value)} data-testid="filter-min-price" className="border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
          <input type="number" placeholder={PRICE_BOUNDS[1]} value={filters.max_price} onChange={(e) => setFilter("max_price", e.target.value)} data-testid="filter-max-price" className="border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3 block">Baujahr</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder={YEAR_BOUNDS[0]} value={filters.min_year} onChange={(e) => setFilter("min_year", e.target.value)} data-testid="filter-min-year" className="border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
          <input type="number" placeholder={YEAR_BOUNDS[1]} value={filters.max_year} onChange={(e) => setFilter("max_year", e.target.value)} data-testid="filter-max-year" className="border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="inventory-page">
      <div className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4 animate-fade-up">
            <span className="divider-thin" />Unser Bestand
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-6xl tracking-tight text-slate-900 leading-[1.05] animate-fade-up delay-100">
            Fahrzeuge bei <span className="font-serif italic font-medium">D.R Automobile</span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl animate-fade-up delay-200">Alle verfügbaren Gebrauchtwagen. Filtern Sie nach Ihren Wünschen.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14 grid md:grid-cols-4 gap-10">
        <aside className="hidden md:block md:col-span-1 sticky top-28 self-start border-r border-slate-200 pr-8">
          {FilterPanel}
        </aside>

        <div className="md:col-span-3">
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <div className="text-sm text-slate-600" data-testid="result-count">
              {loading ? "Lade…" : `${vehicles.length} Fahrzeug${vehicles.length === 1 ? "" : "e"}`}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(true)}
                data-testid="mobile-filter-open"
                className="md:hidden flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm"
              >
                <SlidersHorizontal size={14} /> Filter {activeCount > 0 && `(${activeCount})`}
              </button>
              <select
                value={filters.sort}
                onChange={(e) => setFilter("sort", e.target.value)}
                data-testid="sort-select"
                className="border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              >
                <option value="newest">Neueste zuerst</option>
                <option value="price_asc">Preis aufsteigend</option>
                <option value="price_desc">Preis absteigend</option>
                <option value="mileage_asc">Wenig Kilometer</option>
                <option value="year_desc">Jahr (neu → alt)</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] skeleton" />)}
            </div>
          ) : vehicles.length === 0 ? (
            <div className="border border-slate-200 p-12 text-center" data-testid="no-results">
              <p className="text-slate-600">Keine Fahrzeuge mit diesen Filtern gefunden.</p>
              <button onClick={clearAll} className="mt-4 text-sm text-slate-900 underline">Filter zurücksetzen</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((v, i) => (
                <div key={v.id} className="animate-fade-up" style={{ animationDelay: `${Math.min(i * 60, 480)}ms` }}>
                  <VehicleCard vehicle={v} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-heading font-bold text-lg">Filter</h3>
              <button onClick={() => setShowFilters(false)} data-testid="mobile-filter-close"><X /></button>
            </div>
            {FilterPanel}
            <button
              onClick={() => setShowFilters(false)}
              className="mt-8 w-full bg-slate-900 text-white py-3 text-sm font-medium"
              data-testid="mobile-filter-apply"
            >
              {vehicles.length} Ergebnisse anzeigen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-slate-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-slate-900"
      >
        <option value="">Alle</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
